import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { authAPI, campaignAPI, generateAPI, feedbackAPI, creditsAPI } from '@/lib/api';
import { AD_TYPES, TONES, ASPECT_RATIOS, PLATFORM_LIMITS, CREDIT_PLANS, LOADING_MESSAGES, IMAGE_FILTERS } from '@/lib/constants';
import { 
  trackImageGenerated, 
  trackCreditsUsed, 
  trackCreditsPurchased, 
  trackCopiedContent, 
  trackFeedbackSubmitted,
  trackCampaignCreated
} from '@/lib/firebase';
import {
  Sparkles, Upload, Image as ImageIcon, Copy, Check, ThumbsUp, ThumbsDown,
  RotateCcw, Crop, Sun, Contrast, Filter, Download, Trash2, Plus,
  ChevronRight, Menu, X, LogOut, User, CreditCard, History, Settings,
  Wand2, Palette, Hash, Link2, Clock, Edit2, Save, Copy as CopyIcon,
  AlertTriangle, FileDown, Shield, Loader2, Gift
} from 'lucide-react';
import AffiliatePanel from './AffiliatePanel';

const Dashboard = ({ user: initialUser, setUser: setParentUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showAffiliatePanel, setShowAffiliatePanel] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(() => {
    return localStorage.getItem('imageGenTermsAccepted') === 'true';
  });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [copiedField, setCopiedField] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [selectedAdType, setSelectedAdType] = useState(AD_TYPES[0].id);
  const [selectedTone, setSelectedTone] = useState(TONES[0].id);
  const [selectedSubTone, setSelectedSubTone] = useState('none');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('1:1');
  const [userImage, setUserImage] = useState(null);
  const [productImage, setProductImage] = useState(null);
  const [isAnimeStyle, setIsAnimeStyle] = useState(false);
  
  // Image editor state
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState('none');
  
  // Editable results
  const [editableCaptions, setEditableCaptions] = useState([]);
  const [editableHashtags, setEditableHashtags] = useState([]);

  // Load campaigns on mount
  useEffect(() => {
    loadCampaigns();
  }, []);

  // Update user in parent when changed
  useEffect(() => {
    if (setParentUser) setParentUser(user);
  }, [user, setParentUser]);

  const loadCampaigns = async () => {
    try {
      const response = await campaignAPI.getAll();
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'user') {
          setUserImage(reader.result);
        } else {
          setProductImage(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAcceptTerms = () => {
    localStorage.setItem('imageGenTermsAccepted', 'true');
    setTermsAccepted(true);
    setShowTermsModal(false);
    // Proceed with generation after accepting terms
    proceedWithGeneration();
  };

  const handleGenerate = async () => {
    if (!userImage && !productImage) {
      toast.error('Please upload at least one image');
      return;
    }

    if (user.credits < 5) {
      toast.error('Insufficient credits. Please purchase more.');
      setShowCreditModal(true);
      return;
    }

    // Check if terms are accepted
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }

    proceedWithGeneration();
  };

  const proceedWithGeneration = async () => {
    setIsGenerating(true);
    setLoadingStep(0);

    // Animate through loading messages
    const interval = setInterval(() => {
      setLoadingStep(prev => (prev < LOADING_MESSAGES.length - 1 ? prev + 1 : prev));
    }, 2000);

    try {
      // Create campaign first
      const campaignResponse = await campaignAPI.create({
        name: campaignName || `Campaign ${new Date().toLocaleDateString()}`,
        ad_type: selectedAdType,
        tone: selectedTone,
        sub_tone: selectedSubTone === 'none' ? null : selectedSubTone,
        aspect_ratio: selectedAspectRatio,
        user_image: userImage,
        product_image: productImage,
        is_anime_style: isAnimeStyle
      });

      const campaign = campaignResponse.data;

      // Generate content
      const genResponse = await generateAPI.generate({
        campaign_id: campaign.campaign_id,
        user_image: userImage,
        product_image: productImage,
        ad_type: selectedAdType,
        tone: selectedTone,
        sub_tone: selectedSubTone === 'none' ? null : selectedSubTone,
        aspect_ratio: selectedAspectRatio,
        is_anime_style: isAnimeStyle
      });

      clearInterval(interval);
      
      // Update user credits
      setUser(prev => ({
        ...prev,
        credits: genResponse.data.credits_remaining
      }));

      // Show credit deduction toast
      toast.success(`5 credits deducted. ${genResponse.data.credits_remaining} remaining.`);

      // Track analytics events
      trackCampaignCreated(selectedAdType, selectedTone);
      trackImageGenerated(selectedAdType, selectedAspectRatio);
      trackCreditsUsed(5, 'campaign_generation');

      // Set the generated campaign
      setSelectedCampaign(genResponse.data.campaign);
      setEditableCaptions(genResponse.data.campaign.captions || []);
      setEditableHashtags(genResponse.data.campaign.hashtags || []);

      // Reload campaigns
      loadCampaigns();

    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Generation failed. Please try again.');
    } finally {
      clearInterval(interval);
      setIsGenerating(false);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied ✓');
      // Track copy event
      trackCopiedContent(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleFeedback = async (type) => {
    if (!selectedCampaign) return;
    
    try {
      await feedbackAPI.submit(selectedCampaign.campaign_id, type);
      setSelectedCampaign(prev => ({ ...prev, feedback: type }));
      toast.success('Thank you for your feedback!');
      // Track feedback analytics
      trackFeedbackSubmitted(type);
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  // Function to load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePurchase = async (plan) => {
    setIsProcessingPayment(true);
    
    // Close the credit modal first to prevent UI issues on mobile
    setShowCreditModal(false);
    
    try {
      // Load Razorpay script first
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please check your internet connection.');
        setIsProcessingPayment(false);
        setShowCreditModal(true);
        return;
      }

      // Create Razorpay order
      const orderResponse = await creditsAPI.createOrder(plan);
      const orderData = orderResponse.data;
      
      // Track purchase attempt
      const planDetails = CREDIT_PLANS.find(p => p.id === plan);
      if (planDetails) {
        trackCreditsPurchased(plan, planDetails.price);
      }
      
      setIsProcessingPayment(false);
      
      // Initialize Razorpay checkout with mobile-friendly options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Caption Wizard',
        description: `${orderData.credits} Credits`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            const verifyResponse = await creditsAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            // Update user credits
            setUser(prev => ({
              ...prev,
              credits: verifyResponse.data.total_credits
            }));
            
            toast.success(verifyResponse.data.message);
          } catch (err) {
            console.error('Payment verification failed:', err);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
          contact: ''
        },
        notes: {
          user_id: user?.user_id || '',
          plan: plan
        },
        theme: {
          color: '#9333EA',
          backdrop_color: 'rgba(0, 0, 0, 0.8)'
        },
        modal: {
          confirm_close: true,
          escape: true,
          animation: true,
          backdropclose: false,
          handleback: true,
          ondismiss: () => {
            toast.info('Payment cancelled');
          }
        },
        retry: {
          enabled: true,
          max_count: 3
        },
        config: {
          display: {
            blocks: {
              banks: {
                name: 'Pay via UPI/Cards/Netbanking',
                instruments: [
                  { method: 'upi' },
                  { method: 'card' },
                  { method: 'netbanking' },
                  { method: 'wallet' }
                ]
              }
            },
            sequence: ['block.banks'],
            preferences: {
              show_default_blocks: true
            }
          }
        }
      };
      
      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      
      // Handle payment failure
      razorpay.on('payment.failed', (response) => {
        console.error('Payment failed:', response.error);
        toast.error(response.error.description || 'Payment failed. Please try again.');
      });
      
      razorpay.open();
    } catch (error) {
      console.error('Purchase error:', error);
      setIsProcessingPayment(false);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
    }
  };

  const handleDuplicate = async (campaignId) => {
    try {
      await campaignAPI.duplicate(campaignId);
      loadCampaigns();
      toast.success('Campaign duplicated!');
    } catch (error) {
      toast.error('Failed to duplicate campaign');
    }
  };

  const handleDelete = async (campaignId) => {
    try {
      await campaignAPI.delete(campaignId);
      if (selectedCampaign?.campaign_id === campaignId) {
        setSelectedCampaign(null);
      }
      loadCampaigns();
      toast.success('Campaign deleted!');
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsDeleting(true);
    try {
      await authAPI.deleteAccount();
      toast.success('Account permanently deleted');
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Delete account failed:', error);
      toast.error('Failed to delete account');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
    }
  };

  const handleExportData = async () => {
    try {
      const response = await authAPI.exportData();
      const data = response.data;
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `adgen-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleSaveCampaign = async () => {
    if (!selectedCampaign) return;
    
    try {
      await campaignAPI.update(selectedCampaign.campaign_id, {
        name: selectedCampaign.name,
        captions: editableCaptions,
        hashtags: editableHashtags
      });
      toast.success('Campaign saved!');
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const updateCaption = (index, newText) => {
    setEditableCaptions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text: newText };
      return updated;
    });
  };

  const getSelectedTone = () => TONES.find(t => t.id === selectedTone);
  const getSelectedAdType = () => AD_TYPES.find(t => t.id === selectedAdType);

  const getImageStyle = () => {
    const filter = IMAGE_FILTERS.find(f => f.id === selectedFilter)?.filter || '';
    return {
      filter: `brightness(${brightness}%) contrast(${contrast}%) ${filter}`,
      transform: `rotate(${rotation}deg)`
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <Toaster position="top-right" />
      
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-400" />
              <span className="text-xl font-bold text-white">AdGen AI</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Credits Display */}
            <Button
              variant="outline"
              className="bg-purple-500/20 border-purple-500/50 text-purple-300 hover:bg-purple-500/30"
              onClick={() => setShowCreditModal(true)}
              data-testid="credits-btn"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {user?.credits || 0} Credits
            </Button>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-purple-600 text-white">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-white hidden md:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowCreditModal(true)}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Purchase Credits
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowAffiliatePanel(true)}>
                  <Gift className="w-4 h-4 mr-2" />
                  Affiliate Program
                  <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">10%</Badge>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowPrivacyModal(true)}>
                  <Shield className="w-4 h-4 mr-2" />
                  Privacy & Data
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Campaign History */}
        <aside className={`${sidebarOpen ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-white/10 bg-black/20 backdrop-blur-sm`}>
          <div className="p-4 h-[calc(100vh-64px)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-semibold flex items-center gap-2">
                <History className="w-4 h-4" />
                Campaign History
              </h2>
              <Button
                size="sm"
                variant="ghost"
                className="text-purple-400 hover:text-purple-300"
                onClick={() => {
                  setSelectedCampaign(null);
                  setCampaignName('');
                  setUserImage(null);
                  setProductImage(null);
                }}
                data-testid="new-campaign-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            <ScrollArea className="h-[calc(100%-60px)]">
              <div className="space-y-2">
                {campaigns.map((campaign) => (
                  <div
                    key={campaign.campaign_id}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedCampaign?.campaign_id === campaign.campaign_id
                        ? 'bg-purple-500/30 border border-purple-500/50'
                        : 'bg-white/5 hover:bg-white/10 border border-transparent'
                    }`}
                    onClick={() => {
                      setSelectedCampaign(campaign);
                      setEditableCaptions(campaign.captions || []);
                      setEditableHashtags(campaign.hashtags || []);
                    }}
                    data-testid={`campaign-item-${campaign.campaign_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {campaign.name}
                        </p>
                        <p className="text-gray-400 text-xs mt-1">
                          {new Date(campaign.created_at).toLocaleDateString()}
                        </p>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {AD_TYPES.find(t => t.id === campaign.ad_type)?.icon} {campaign.ad_type}
                        </Badge>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-6 w-6">
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleDuplicate(campaign.campaign_id)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(campaign.campaign_id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
                
                {campaigns.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-400 text-sm">No campaigns yet</p>
                    <p className="text-gray-500 text-xs mt-1">Create your first campaign!</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-6xl mx-auto">
            {/* Generation Form */}
            {!selectedCampaign?.generated_image && (
              <Card className="bg-white/5 backdrop-blur-sm border-white/10 mb-6">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Wand2 className="w-5 h-5 text-purple-400" />
                    Create New Campaign
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Campaign Name */}
                  <div>
                    <Label className="text-gray-300">Campaign Name</Label>
                    <Input
                      placeholder="My Awesome Campaign"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      className="bg-white/10 border-white/20 text-white mt-1"
                      data-testid="campaign-name-input"
                    />
                  </div>

                  {/* Image Uploads */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* User Image */}
                    <div>
                      <Label className="text-gray-300">User/Model Image</Label>
                      <div className="mt-2 relative">
                        {userImage ? (
                          <div className="relative group">
                            <img
                              src={userImage}
                              alt="User"
                              className="w-full h-48 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setEditingImage('user');
                                  setShowImageEditor(true);
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setUserImage(null)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors">
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <span className="text-gray-400 text-sm">Upload user image</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleImageUpload(e, 'user')}
                              data-testid="user-image-input"
                            />
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Product Image */}
                    <div>
                      <Label className="text-gray-300">Product Image</Label>
                      <div className="mt-2 relative">
                        {productImage ? (
                          <div className="relative group">
                            <img
                              src={productImage}
                              alt="Product"
                              className="w-full h-48 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setEditingImage('product');
                                  setShowImageEditor(true);
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setProductImage(null)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-purple-500/50 transition-colors">
                            <Upload className="w-8 h-8 text-gray-400 mb-2" />
                            <span className="text-gray-400 text-sm">Upload product image</span>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleImageUpload(e, 'product')}
                              data-testid="product-image-input"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Ad Type Selection */}
                  <div>
                    <Label className="text-gray-300">Ad Type</Label>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-2">
                      {AD_TYPES.map((type) => (
                        <button
                          key={type.id}
                          onClick={() => setSelectedAdType(type.id)}
                          className={`p-3 rounded-lg text-center transition-all ${
                            selectedAdType === type.id
                              ? 'bg-purple-500/30 border-2 border-purple-500'
                              : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                          }`}
                          data-testid={`ad-type-${type.id}`}
                        >
                          <span className="text-2xl block mb-1">{type.icon}</span>
                          <span className="text-white text-xs font-medium">{type.name}</span>
                        </button>
                      ))}
                    </div>
                    {getSelectedAdType() && (
                      <p className="text-gray-400 text-sm mt-2">
                        {getSelectedAdType().description}
                      </p>
                    )}
                  </div>

                  {/* Tone & Sub-tone */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Tone</Label>
                      <Select value={selectedTone} onValueChange={setSelectedTone}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1" data-testid="tone-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TONES.map((tone) => (
                            <SelectItem key={tone.id} value={tone.id}>
                              {tone.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Sub-tone (Optional)</Label>
                      <Select value={selectedSubTone} onValueChange={setSelectedSubTone}>
                        <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1" data-testid="subtone-select">
                          <SelectValue placeholder="Select sub-tone" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {getSelectedTone()?.subTones?.map((sub) => (
                            <SelectItem key={sub} value={sub}>
                              {sub}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Aspect Ratio */}
                  <div>
                    <Label className="text-gray-300">Aspect Ratio</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {ASPECT_RATIOS.map((ratio) => (
                        <button
                          key={ratio.id}
                          onClick={() => setSelectedAspectRatio(ratio.id)}
                          className={`p-3 rounded-lg text-center transition-all ${
                            selectedAspectRatio === ratio.id
                              ? 'bg-purple-500/30 border-2 border-purple-500'
                              : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                          }`}
                          data-testid={`aspect-ratio-${ratio.id}`}
                        >
                          <span className="text-white text-sm font-medium">{ratio.name}</span>
                          <span className="text-gray-400 text-xs block">{ratio.description}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Anime Style Toggle */}
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                    <div>
                      <Label className="text-white">Anime / Illustration Style</Label>
                      <p className="text-gray-400 text-sm">Generate in anime/illustrated aesthetic</p>
                    </div>
                    <Switch
                      checked={isAnimeStyle}
                      onCheckedChange={setIsAnimeStyle}
                      data-testid="anime-style-toggle"
                    />
                  </div>

                  {/* Generate Button */}
                  <Button
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 text-lg"
                    onClick={handleGenerate}
                    disabled={isGenerating || (!userImage && !productImage)}
                    data-testid="generate-btn"
                  >
                    {isGenerating ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                          <span>Generating...</span>
                        </div>
                        <span className="text-sm mt-1 opacity-80">{LOADING_MESSAGES[loadingStep]}</span>
                      </div>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Campaign (5 Credits)
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Results Display */}
            {selectedCampaign && (selectedCampaign.generated_image || selectedCampaign.captions?.length > 0) && (
              <div className="space-y-6">
                {/* Campaign Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Input
                      value={selectedCampaign.name}
                      onChange={(e) => setSelectedCampaign(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-transparent border-none text-2xl font-bold text-white p-0 focus-visible:ring-0"
                      data-testid="campaign-name-edit"
                    />
                    <Button size="sm" variant="ghost" onClick={handleSaveCampaign}>
                      <Save className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={selectedCampaign.feedback === 'thumbs_up' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleFeedback('thumbs_up')}
                      className={selectedCampaign.feedback === 'thumbs_up' ? 'bg-green-600' : ''}
                      data-testid="thumbs-up-btn"
                    >
                      <ThumbsUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant={selectedCampaign.feedback === 'thumbs_down' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleFeedback('thumbs_down')}
                      className={selectedCampaign.feedback === 'thumbs_down' ? 'bg-red-600' : ''}
                      data-testid="thumbs-down-btn"
                    >
                      <ThumbsDown className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Generated Image */}
                  {selectedCampaign.generated_image && (
                    <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <ImageIcon className="w-5 h-5 text-purple-400" />
                          Generated Ad Image
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <img
                          src={`data:image/png;base64,${selectedCampaign.generated_image}`}
                          alt="Generated Ad"
                          className="w-full rounded-lg"
                          data-testid="generated-image"
                        />
                        <div className="flex gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `data:image/png;base64,${selectedCampaign.generated_image}`;
                              link.download = 'ad-image.png';
                              link.click();
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Captions & Hashtags */}
                  <div className="space-y-4">
                    {/* Captions */}
                    <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Palette className="w-5 h-5 text-pink-400" />
                          Captions
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Tabs defaultValue={editableCaptions[0]?.platform || 'X'}>
                          <TabsList className="bg-white/10">
                            {editableCaptions.map((caption) => (
                              <TabsTrigger key={caption.platform} value={caption.platform}>
                                {caption.platform}
                              </TabsTrigger>
                            ))}
                          </TabsList>
                          {editableCaptions.map((caption, index) => (
                            <TabsContent key={caption.platform} value={caption.platform}>
                              <div className="space-y-2">
                                <Textarea
                                  value={caption.text}
                                  onChange={(e) => updateCaption(index, e.target.value)}
                                  className="bg-white/10 border-white/20 text-white min-h-[120px]"
                                  data-testid={`caption-${caption.platform}`}
                                />
                                <div className="flex items-center justify-between">
                                  <span className={`text-sm ${
                                    caption.text.length > (PLATFORM_LIMITS[caption.platform] || 280)
                                      ? 'text-red-400'
                                      : 'text-gray-400'
                                  }`}>
                                    {caption.text.length} / {PLATFORM_LIMITS[caption.platform] || caption.character_limit}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleCopy(caption.text, `caption-${caption.platform}`)}
                                    data-testid={`copy-caption-${caption.platform}`}
                                  >
                                    {copiedField === `caption-${caption.platform}` ? (
                                      <Check className="w-4 h-4 text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>

                    {/* Hashtags */}
                    <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Hash className="w-5 h-5 text-blue-400" />
                          Hashtags
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {editableHashtags.map((tag, index) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="bg-blue-500/20 text-blue-300 cursor-pointer hover:bg-blue-500/30"
                              data-testid={`hashtag-${index}`}
                            >
                              {tag.startsWith('#') ? tag : `#${tag}`}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">
                            {editableHashtags.length} hashtags
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCopy(editableHashtags.map(t => t.startsWith('#') ? t : `#${t}`).join(' '), 'hashtags')}
                            data-testid="copy-hashtags-btn"
                          >
                            {copiedField === 'hashtags' ? (
                              <Check className="w-4 h-4 text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Product Link */}
                    {selectedCampaign.product_link && (
                      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <Link2 className="w-5 h-5 text-green-400" />
                            Product Link
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-2">
                            <Input
                              value={selectedCampaign.product_link}
                              readOnly
                              className="bg-white/10 border-white/20 text-white"
                              data-testid="product-link"
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleCopy(selectedCampaign.product_link, 'product-link')}
                              data-testid="copy-product-link-btn"
                            >
                              {copiedField === 'product-link' ? (
                                <Check className="w-4 h-4 text-green-400" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* CTA */}
                    {selectedCampaign.cta && (
                      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                        <CardHeader>
                          <CardTitle className="text-white">Call to Action</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Badge variant="default" className="bg-gradient-to-r from-purple-600 to-pink-600 text-lg px-4 py-2">
                            {selectedCampaign.cta}
                          </Badge>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Create New Button */}
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedCampaign(null);
                      setCampaignName('');
                      setUserImage(null);
                      setProductImage(null);
                    }}
                    data-testid="create-new-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Campaign
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Credit Purchase Modal */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className="bg-gray-900 border-white/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">💳 Choose Your Plan</DialogTitle>
            <DialogDescription className="text-gray-400">
              Select a plan to continue creating stunning ad images
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-purple-500/20 rounded-lg">
              <span className="text-gray-300">Current Balance</span>
              <span className="text-2xl font-bold text-purple-400">{user?.credits || 0} Images</span>
            </div>
            
            {user?.credits === 0 && (
              <div className="p-3 bg-amber-500/20 rounded-lg border border-amber-500/30">
                <p className="text-amber-300 text-sm text-center">
                  You've used your free images! Choose a plan to continue.
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {CREDIT_PLANS.map((plan) => (
                <Card
                  key={plan.id}
                  className={`bg-white/5 border-white/20 cursor-pointer hover:border-purple-500/50 transition-all touch-manipulation relative ${
                    plan.popular ? 'ring-2 ring-purple-500 scale-[1.02]' : ''
                  } ${isProcessingPayment ? 'opacity-50 pointer-events-none' : ''}`}
                  onClick={() => !isProcessingPayment && handlePurchase(plan.id)}
                  data-testid={`credit-plan-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-purple-600">⭐ Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="pb-2 pt-4">
                    <div className="flex items-center gap-2">
                      {plan.id === 'starter' && <span>🟢</span>}
                      {plan.id === 'creator' && <span>⭐</span>}
                      {plan.id === 'pro' && <span>🟣</span>}
                      <CardTitle className="text-white text-lg">{plan.name}</CardTitle>
                    </div>
                    <p className="text-gray-500 text-xs">{plan.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3">
                      <span className="text-2xl sm:text-3xl font-bold text-white">₹{plan.price}</span>
                      <span className="text-gray-400 text-sm"> / month</span>
                    </div>
                    
                    <ul className="space-y-1 mb-4">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="text-gray-400 text-xs flex items-center gap-1">
                          <span className="text-green-400">✓</span> {feature}
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-purple-600 hover:bg-purple-700' : 'bg-white/10 hover:bg-white/20'} active:scale-95 touch-manipulation`}
                      disabled={isProcessingPayment}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isProcessingPayment) handlePurchase(plan.id);
                      }}
                    >
                      {isProcessingPayment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          {plan.id === 'starter' ? 'Get Started' : plan.id === 'creator' ? 'Upgrade Now' : 'Go Pro'}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center text-gray-500 text-sm">
              <p>Secure payment powered by Razorpay</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Editor Modal */}
      <Dialog open={showImageEditor} onOpenChange={setShowImageEditor}>
        <DialogContent className="bg-gray-900 border-white/20 text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Preview */}
            <div className="flex justify-center bg-black/50 rounded-lg p-4">
              <img
                src={editingImage === 'user' ? userImage : productImage}
                alt="Edit preview"
                className="max-h-64 rounded-lg"
                style={getImageStyle()}
              />
            </div>

            {/* Controls */}
            <div className="space-y-4">
              {/* Brightness */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Sun className="w-4 h-4" />
                  Brightness: {brightness}%
                </Label>
                <Slider
                  value={[brightness]}
                  onValueChange={([v]) => setBrightness(v)}
                  min={50}
                  max={150}
                  step={1}
                  className="mt-2"
                />
              </div>

              {/* Contrast */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Contrast className="w-4 h-4" />
                  Contrast: {contrast}%
                </Label>
                <Slider
                  value={[contrast]}
                  onValueChange={([v]) => setContrast(v)}
                  min={50}
                  max={150}
                  step={1}
                  className="mt-2"
                />
              </div>

              {/* Rotation */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <RotateCcw className="w-4 h-4" />
                  Rotation: {rotation}°
                </Label>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" onClick={() => setRotation(r => r - 90)}>
                    -90°
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setRotation(0)}>
                    Reset
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setRotation(r => r + 90)}>
                    +90°
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div>
                <Label className="text-gray-300 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                </Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {IMAGE_FILTERS.map((filter) => (
                    <Button
                      key={filter.id}
                      variant={selectedFilter === filter.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedFilter(filter.id)}
                      className={selectedFilter === filter.id ? 'bg-purple-600' : ''}
                    >
                      {filter.name}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setBrightness(100);
                setContrast(100);
                setRotation(0);
                setSelectedFilter('none');
              }}>
                Reset All
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => {
                  // Apply edits would require canvas manipulation
                  // For now, just close the modal
                  setShowImageEditor(false);
                  toast.success('Image edited! (Preview only)');
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy & Data Modal */}
      <Dialog open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
        <DialogContent className="bg-gray-900 border-white/20 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              Privacy & Data
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Manage your data and privacy settings. We take your privacy seriously.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Data Export */}
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <FileDown className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-white">Export Your Data</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Download all your data including campaigns, images, and account information in JSON format.
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={handleExportData}
                    data-testid="export-data-btn"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Download My Data
                  </Button>
                </div>
              </div>
            </div>

            {/* GDPR Info */}
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <h3 className="font-medium text-white mb-2">Your Rights (GDPR)</h3>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>✓ Right to access your data</li>
                <li>✓ Right to download/export your data</li>
                <li>✓ Right to delete your data</li>
                <li>✓ Right to withdraw consent</li>
              </ul>
            </div>

            {/* Delete Account */}
            <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/30">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-red-400">Delete Account</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-account-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete My Account
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-gray-900 border-white/20 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Delete Account Permanently
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              <div className="space-y-4 mt-2">
                <p className="text-sm">
                  When you delete your account, all uploaded images, generated content, and personal data are permanently removed from our systems and cannot be recovered.
                </p>
                <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                  <p className="text-sm text-red-300 font-medium">This will permanently delete:</p>
                  <ul className="text-sm text-gray-400 mt-2 space-y-1">
                    <li>• Your profile and account information</li>
                    <li>• All uploaded images (user & product)</li>
                    <li>• All generated ad images</li>
                    <li>• All captions, hashtags, and campaigns</li>
                    <li>• Purchase history and remaining credits</li>
                  </ul>
                </div>
                <div className="mt-4">
                  <Label className="text-gray-300 text-sm">
                    Type <span className="font-bold text-red-400">DELETE</span> to confirm:
                  </Label>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE"
                    className="mt-2 bg-white/10 border-white/20 text-white"
                    data-testid="delete-confirm-input"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={() => {
                setDeleteConfirmText('');
                setShowDeleteConfirm(false);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'DELETE' || isDeleting}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Forever
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Image Generation Terms of Use Modal */}
      <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
        <DialogContent className="bg-gray-900 border-white/20 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              Image Generation Terms of Use
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* How It Works */}
            <div className="space-y-3">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-purple-400" />
                How It Works
              </h3>
              <ul className="text-gray-300 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span><strong>Instant Generation:</strong> Images are created on-demand and are not stored on our servers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span><strong>Immediate Download:</strong> Download your generated image right away</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span><strong>Usage Limits:</strong></span>
                </li>
                <ul className="ml-6 text-gray-400 text-sm space-y-1">
                  <li>- Free users: Up to 1 image per day</li>
                  <li>- Paid users: Higher daily limits available</li>
                </ul>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span><strong>Credit System:</strong> Each image generation consumes AI credits from your account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-400">•</span>
                  <span><strong>Service Protection:</strong> Generating too many images in a short period may temporarily pause the service to ensure quality for all users</span>
                </li>
              </ul>
            </div>

            {/* Privacy & Data */}
            <div className="space-y-3">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                Privacy & Data
              </h3>
              <p className="text-gray-300 text-sm">
                By using this service, you acknowledge that:
              </p>
              <ul className="text-gray-300 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>We do not store your generated images on our servers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>We do not share your images with third parties</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>Images are your responsibility to save locally</span>
                </li>
              </ul>
            </div>

            {/* Agreement Notice */}
            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
              <p className="text-purple-300 text-sm font-medium text-center">
                By continuing, you agree to these terms.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowTermsModal(false)}
              className="border-white/20"
            >
              Cancel
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleAcceptTerms}
              data-testid="accept-terms-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              I Agree & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Affiliate Program Modal */}
      <Dialog open={showAffiliatePanel} onOpenChange={setShowAffiliatePanel}>
        <DialogContent className="bg-gray-900 border-white/20 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <AffiliatePanel user={user} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
